import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  useMediaQuery,
} from '@mui/material';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import WifiIcon from '@mui/icons-material/Wifi';
import { getUserMetadata } from '../../../../services/user.service';
import useDateTimeFormat from '../../../../hooks/useDateTimeFormat';

interface DeviceMetadata {
  imei: string;
  make: string;
  model: string;
  osVersion: string;
  appVersion: string;
  networkType: string;
  batteryPercentage: number;
  modifiedDateTime?: string;
}

// Utility functions
const getBatteryColor = (battery: number) => {
  if (battery > 60) return 'success.main';
  if (battery > 30) return 'warning.main';
  return 'error.main';
};

const getNetworkChipColor = (type: string) => {
  switch (type.toLowerCase()) {
    case '5g':
      return 'success';
    case '4g':
    case 'lte':
      return 'primary';
    case '3g':
      return 'warning';
    case 'wifi':
      return 'secondary';
    default:
      return 'default';
  }
};

const UserMetadata = ({ userId }: { userId: string }) => {
  const { toStandardDateWithSeconds, withTimeMode } = useDateTimeFormat();

  const formatDate = (date?: string) => {
    if (!date) return '-';

    const formatted = toStandardDateWithSeconds(date);

    return formatted ? String(formatted) : '-';
  };

  const [mobileDevices, setMobileDevices] = useState<DeviceMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width:900px)');

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const res = await getUserMetadata(userId);
        const mappedDevices: DeviceMetadata[] = res.results.map((item) => ({
          imei: item.values?.imei ?? '-', // fallback to ID if IMEI not present
          make: item.values?.make || '-',
          model: item.values?.model || '-',
          osVersion: item.values?.os || '-',
          appVersion: item.values?.appversion || '-',
          networkType: item.values?.networkType || '-',
          batteryPercentage: parseInt(item.values?.batteryPercentage) || 0,
          modifiedDateTime: item.modifiedDateTime ?? '',
        }));

        setMobileDevices(mappedDevices);
      } catch (err) {
        console.error('Failed to fetch device metadata:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMetadata();
  }, [userId]);

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (mobileDevices.length === 0) {
    return (
      <Box textAlign='center' py={4}>
        <SmartphoneIcon fontSize='large' color='disabled' />
        <Typography variant='body2' color='textSecondary'>
          No device metadata available
        </Typography>
      </Box>
    );
  }

  return (
    <Card variant='outlined'>
      <CardHeader
        title={
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Box display='flex' alignItems='center' gap={1}>
              <SmartphoneIcon />
              <Typography variant='h6'>Device Metadata ({mobileDevices.length})</Typography>
            </Box>
            <Chip label='Unique by IMEI' variant='outlined' size='small' />
          </Box>
        }
      />
      <CardContent>
        {isMobile ? (
          // Mobile View
          <Box display='flex' flexDirection='column' gap={2}>
            {mobileDevices.map((device) => (
              <Card key={device.imei} variant='outlined'>
                <CardContent>
                  <Typography variant='subtitle1' fontWeight='bold'>
                    {device.make} {device.model}
                  </Typography>
                  <Chip label={device.networkType} color={getNetworkChipColor(device.networkType)} size='small' sx={{ mt: 1 }} />

                  <Box mt={2}>
                    <Typography variant='caption' color='textSecondary'>
                      IMEI
                    </Typography>
                    <Typography variant='body2' fontFamily='monospace'>
                      {device.imei}
                    </Typography>
                  </Box>

                  <Box mt={1}>
                    <Typography variant='caption' color='textSecondary'>
                      OS Version
                    </Typography>
                    <Typography variant='body2'>{device.osVersion}</Typography>
                  </Box>

                  <Box mt={1}>
                    <Typography variant='caption' color='textSecondary'>
                      App Version
                    </Typography>
                    <Typography variant='body2'>{device.appVersion}</Typography>
                  </Box>

                  <Box mt={1} display='flex' alignItems='center' gap={1}>
                    <BatteryFullIcon sx={{ fontSize: 18, color: getBatteryColor(device.batteryPercentage) }} />
                    <Typography variant='body2' sx={{ color: getBatteryColor(device.batteryPercentage) }}>
                      {device.batteryPercentage}%
                    </Typography>
                  </Box>

                  {device.modifiedDateTime && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant='caption' color='textSecondary'>
                        {withTimeMode('Modified')}: {formatDate(device.modifiedDateTime)}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          // Desktop View
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>IMEI</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>OS Version</TableCell>
                  <TableCell>App Version</TableCell>
                  <TableCell>Network Type</TableCell>
                  <TableCell>Battery Percentage</TableCell>
                  <TableCell>{withTimeMode('Modified')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mobileDevices.map((device) => (
                  <TableRow key={device.imei}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{device.imei}</TableCell>
                    <TableCell>
                      {device.make} {device.model}
                    </TableCell>
                    <TableCell>{device.osVersion}</TableCell>
                    <TableCell>{device.appVersion}</TableCell>
                    <TableCell>
                      <Chip
                        label={device.networkType}
                        color={getNetworkChipColor(device.networkType)}
                        icon={<WifiIcon sx={{ fontSize: 16 }} />}
                        size='small'
                      />
                    </TableCell>
                    <TableCell>
                      <Box display='flex' alignItems='center' gap={1}>
                        <BatteryFullIcon sx={{ fontSize: 18, color: getBatteryColor(device.batteryPercentage) }} />
                        <Typography variant='body2' sx={{ color: getBatteryColor(device.batteryPercentage) }}>
                          {device.batteryPercentage}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(device.modifiedDateTime)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default UserMetadata;
